import { range } from "lodash-es";
import { useMemo, useState } from "react";

const MAX_RANGE = 100;

/**
 * Manages the state and validation for two number inputs representing a range.
 * Calculates the resulting array of IDs if the range is valid.
 *
 * @returns An object containing:
 *  - `numberInputFrom`, `numberInputTo`: Current state values for the inputs.
 *  - `setNumberInputFrom`, `setNumberInputTo`: State setters for the inputs.
 *  - `errorInputFrom`, `errorInputTo`: Validation error messages for the inputs (null if valid).
 *  - `idRange`: An array of strings representing the numeric range (e.g., ["5", "6", "7"])
 *               if both inputs are valid and form a correct range, otherwise null. Memoized.
 */
export function useNumberInputRange() {
  const [numberInputFrom, setNumberInputFrom] = useState<number | undefined>();
  const [numberInputTo, setNumberInputTo] = useState<number | undefined>();

  const errorInputFrom = useMemo(() => {
    if (numberInputTo !== undefined && numberInputFrom === undefined) {
      return "Bitte geben Sie eine Startnummer ein";
    }
    return null;
  }, [numberInputFrom, numberInputTo]);

  const errorInputTo = useMemo(() => {
    if (numberInputFrom !== undefined && numberInputTo === undefined) {
      return "Bitte geben Sie eine Endnummer ein";
    } else if (numberInputFrom !== undefined && numberInputTo !== undefined) {
      if (numberInputFrom > numberInputTo) {
        return "Die Endnummer muss größer als die Startnummer sein";
      } else if (numberInputTo - numberInputFrom >= MAX_RANGE) {
        // Check if the number of items in the range exceeds the maximum allowed
        return `Es können maximal ${MAX_RANGE} Behälter ausgewählt werden`;
      }
    }
    return null;
  }, [numberInputFrom, numberInputTo]);

  const idRange = useMemo(() => {
    const fromToIsSet = numberInputFrom !== undefined && numberInputTo !== undefined;
    const noErrors = !errorInputFrom && !errorInputTo;

    if (fromToIsSet && noErrors) {
      // +1 because lodash range() excludes the end value
      return range(numberInputFrom, numberInputTo + 1).map((it) => it.toString());
    }
    // Return null if range is not valid or inputs are incomplete
    return null;
  }, [numberInputFrom, numberInputTo, errorInputFrom, errorInputTo]);

  return {
    numberInputFrom,
    numberInputTo,
    setNumberInputFrom,
    setNumberInputTo,
    errorInputFrom,
    errorInputTo,
    idRange,
  };
}
